import React, { useState, useId, useRef, useEffect, Fragment } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthContext } from './hooks/useAuthContext';
import BeeIcon from '../images/ReeBee';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { AnimatePresence, motion } from 'framer-motion';
import clsx from 'clsx';

// --- Helper Hooks ---
function useDebounce(callback, delay) {
    const callbackRef = useRef(callback);
    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    const timerRef = useRef();

    const debouncedCallback = (...args) => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => {
            callbackRef.current(...args);
        }, delay);
    };

    return debouncedCallback;
}

function usePrevious(value) {
    let ref = useRef(undefined);

    useEffect(() => {
        ref.current = value;
    }, [value]);

    return ref.current;
}

// --- Icons ---
function StarIcon(props) {
    return (
        <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" {...props}>
            <path fillRule="evenodd" d="M10.868 2.884c-.321-.772-1.415-.772-1.736 0l-1.83 4.401-4.753.381c-.833.067-1.171 1.107-.536 1.651l3.62 3.102-1.106 4.637c-.194.813.691 1.456 1.405 1.02L10 15.591l4.069 2.485c.713.436 1.598-.207 1.404-1.02l-1.106-4.637 3.62-3.102c.635-.544.297-1.584-.536-1.65l-4.752-.382-1.831-4.401z" clipRule="evenodd" />
        </svg>
    )
}

function AppleIcon(props) {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
        </svg>
    );
}

function PulseIcon(props) {
    return (
        <svg viewBox="0 0 32 32" aria-hidden="true" {...props}>
            <circle cx={16} cy={16} r={16} fill="#FACC15" fillOpacity={0.2} />
            <path d="M8 16h4l2-6 4 12 2-6h4" stroke="#FACC15" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function ChartIcon(props) {
    return (
        <svg viewBox="0 0 32 32" aria-hidden="true" {...props}>
            <circle cx={16} cy={16} r={16} fill="#FACC15" fillOpacity={0.2} />
            <path d="M9 22l4-4 4 4 6-6" stroke="#FACC15" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
            <path d="M19 16l4-4m0 0h-4m4 0v4" stroke="#FACC15" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function BrainIcon(props) {
    return (
        <svg viewBox="0 0 32 32" aria-hidden="true" {...props}>
            <circle cx={16} cy={16} r={16} fill="#FACC15" fillOpacity={0.2} />
            <path d="M16 10c-3 0-5 2-5 5 0 2 1 3.5 2.5 4.5V23h5v-3.5c1.5-1 2.5-2.5 2.5-4.5 0-3-2-5-5-5z" fill="#FACC15" />
            <path d="M14 23h4v1h-4v-1z" fill="#FACC15" />
        </svg>
    );
}

// --- Components ---

function CircleBackground({ color, className, ...props }) {
    let id = useId();
    return (
        <svg viewBox="0 0 558 558" width="558" height="558" fill="none" aria-hidden="true" className={className} {...props}>
            <defs>
                <linearGradient id={id} x1="79" y1="16" x2="105" y2="237" gradientUnits="userSpaceOnUse">
                    <stop stopColor={color} />
                    <stop offset="1" stopColor={color} stopOpacity="0" />
                </linearGradient>
            </defs>
            <path opacity=".2" d="M1 279C1 125.465 125.465 1 279 1s278 124.465 278 278-124.465 278-278 278S1 432.535 1 279Z" stroke={color} />
            <path d="M1 279C1 125.465 125.465 1 279 1" stroke={`url(#${id})`} strokeLinecap="round" />
        </svg>
    );
}

function BackgroundIllustration(props) {
    const id = useId();
    return (
        <div {...props}>
            <svg viewBox="0 0 1026 1026" fill="none" aria-hidden="true" className="absolute inset-0 h-full w-full animate-[spin_60s_linear_infinite]">
                <path d="M1025 513c0 282.77-229.23 512-512 512S1 795.77 1 513 230.23 1 513 1s512 229.23 512 512Z" stroke="#D4D4D4" strokeOpacity="0.7" />
                <path d="M513 1025C230.23 1025 1 795.77 1 513" stroke={`url(#${id}-gradient-1)`} strokeLinecap="round" />
                <defs>
                    <linearGradient id={`${id}-gradient-1`} x1="1" y1="513" x2="1" y2="1025" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#FACC15" />
                        <stop offset="1" stopColor="#FACC15" stopOpacity="0" />
                    </linearGradient>
                </defs>
            </svg>
            <svg viewBox="0 0 1026 1026" fill="none" aria-hidden="true" className="absolute inset-0 h-full w-full animate-[spin_90s_linear_infinite_reverse]">
                <path d="M913 513c0 220.914-179.086 400-400 400S113 733.914 113 513s179.086-400 400-400 400 179.086 400 400Z" stroke="#D4D4D4" strokeOpacity="0.7" />
                <path d="M913 513c0 220.914-179.086 400-400 400" stroke={`url(#${id}-gradient-2)`} strokeLinecap="round" />
                <defs>
                    <linearGradient id={`${id}-gradient-2`} x1="913" y1="513" x2="913" y2="913" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#FACC15" />
                        <stop offset="1" stopColor="#FACC15" stopOpacity="0" />
                    </linearGradient>
                </defs>
            </svg>
        </div>
    );
}

function PhoneFrame({ className, children, priority = false, ...props }) {
    return (
        <div className={clsx('relative aspect-[366/729]', className)} {...props}>
            {/* Frame Shadow */}
            <div className="absolute inset-y-[calc(1/729*100%)] right-[calc(5/729*100%)] left-[calc(7/729*100%)] rounded-[calc(58/366*100%)/calc(58/729*100%)] shadow-2xl" />

            {/* Screen Container */}
            <div className="absolute top-[calc(23/729*100%)] left-[calc(23/366*100%)] h-[calc(686/729*100%)] w-[calc(318/366*100%)] overflow-hidden rounded-[calc(40/366*100%)/calc(40/729*100%)] bg-gray-900">
                {children}
            </div>

            {/* Frame Border/Image Simulation (Simplified without SVG asset) */}
            <div className="pointer-events-none absolute inset-0 rounded-[calc(58/366*100%)/calc(58/729*100%)] ring-1 ring-inset ring-black/10" />
            <div className="pointer-events-none absolute inset-0 rounded-[calc(58/366*100%)/calc(58/729*100%)] border-[12px] border-gray-900" style={{ clipPath: 'inset(1px)' }} />
        </div>
    )
}


const features = [
    {
        name: 'Build Food & Symptom Diary',
        description: 'Log meals and reactions in seconds to build a comprehensive timeline of your dietary health.',
        icon: PulseIcon,
        image: '/Device-2.png',
    },
    {
        name: 'Spot Reaction Patterns',
        description: 'Visualize reaction patterns across different food groups to isolate broad categories causing issues.',
        icon: ChartIcon,
        image: '/Device-3.png',
    },
    {
        name: 'Pinpoint Specific Ingredients',
        description: 'Find specific ingredients within broad food groups like gluten to know exactly what to avoid.',
        icon: BrainIcon,
        image: '/Device-6.png',
    },
    {
        name: 'Targeted Health Insights',
        description: 'Gain actionable insights from your data to understand your body and manage your diet with confidence.',
        icon: ChartIcon,
        image: '/Device-5.png',
    },
]

function FeaturesDesktop() {
    let [changeCount, setChangeCount] = useState(0)
    let [selectedIndex, setSelectedIndex] = useState(0)
    let prevIndex = usePrevious(selectedIndex)
    let isForwards = prevIndex === undefined ? true : selectedIndex > prevIndex

    let onChange = useDebounce(
        (selectedIndex) => {
            setSelectedIndex(selectedIndex)
            setChangeCount((changeCount) => changeCount + 1)
        },
        100
    )

    return (
        <TabGroup
            className="grid grid-cols-12 items-center gap-8 lg:gap-16 xl:gap-24"
            selectedIndex={selectedIndex}
            onChange={onChange}
            vertical
        >
            <TabList className="relative z-10 order-last col-span-6 space-y-6">
                {features.map((feature, featureIndex) => (
                    <div
                        key={feature.name}
                        className="relative rounded-2xl transition-colors hover:bg-white/60"
                    >
                        {featureIndex === selectedIndex && (
                            <motion.div
                                layoutId="activeBackground"
                                className="absolute inset-0 bg-white shadow-lg ring-1 ring-gray-900/5"
                                initial={{ borderRadius: 16 }}
                            />
                        )}
                        <div className="relative z-10 p-8 cursor-pointer group" onClick={() => onChange(featureIndex)}>
                            <feature.icon className="h-8 w-8" />
                            <h3 className="mt-6 text-lg font-semibold text-gray-900">
                                <Tab className="text-left outline-none focus:outline-none">
                                    <span className="absolute inset-0 rounded-2xl" />
                                    {feature.name}
                                </Tab>
                            </h3>
                            <p className="mt-2 text-sm text-gray-500">
                                {feature.description}
                            </p>
                        </div>
                    </div>
                ))}
            </TabList>
            <div className="relative col-span-6">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                    <CircleBackground color="#FACC15" className="animate-[spin_40s_linear_infinite]" />
                </div>
                <div className="relative z-10 mx-auto w-full max-w-[366px] aspect-[366/729] grid grid-cols-1 grid-rows-1">
                    <TabPanels as={Fragment}>
                        <AnimatePresence
                            initial={false}
                            custom={{ isForwards, changeCount }}
                        >
                            {features.map((feature, featureIndex) =>
                                selectedIndex === featureIndex ? (
                                    <TabPanel
                                        static
                                        key={feature.name}
                                        className="col-start-1 row-start-1 flex focus:outline-none h-full w-full"
                                    >
                                        <motion.div
                                            initial={{ opacity: 0, x: isForwards ? 20 : -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: isForwards ? -20 : 20 }}
                                            transition={{ duration: 0.3 }}
                                            className="h-full w-full"
                                        >
                                            <img
                                                src={feature.image}
                                                alt={feature.name}
                                                className="h-full w-full object-contain"
                                            />
                                        </motion.div>
                                    </TabPanel>
                                ) : null,
                            )}
                        </AnimatePresence>
                    </TabPanels>
                </div>
            </div>
        </TabGroup>
    )
}

function FeaturesMobile() {
    let [activeIndex, setActiveIndex] = useState(0)
    let slideContainerRef = useRef(null)
    let slideRefs = useRef([])

    useEffect(() => {
        let observer = new window.IntersectionObserver(
            (entries) => {
                for (let entry of entries) {
                    if (entry.isIntersecting && entry.target instanceof HTMLDivElement) {
                        setActiveIndex(slideRefs.current.indexOf(entry.target))
                        break
                    }
                }
            },
            {
                root: slideContainerRef.current,
                threshold: 0.6,
            },
        )

        for (let slide of slideRefs.current) {
            if (slide) {
                observer.observe(slide)
            }
        }

        return () => {
            observer.disconnect()
        }
    }, [slideContainerRef, slideRefs])

    return (
        <>
            <div
                ref={slideContainerRef}
                className="-mb-4 flex snap-x snap-mandatory -space-x-4 overflow-x-auto overscroll-x-contain scroll-smooth pb-4 [scrollbar-width:none] sm:-space-x-6 [&::-webkit-scrollbar]:hidden"
            >
                {features.map((feature, featureIndex) => (
                    <div
                        key={featureIndex}
                        ref={(ref) => {
                            if (ref) {
                                slideRefs.current[featureIndex] = ref
                            }
                        }}
                        className="w-full flex-none snap-center px-4 sm:px-6"
                    >
                        <div className="relative transform overflow-hidden rounded-2xl bg-white px-5 py-6 shadow-xl ring-1 ring-gray-900/5">
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                                <CircleBackground
                                    color="#FACC15"
                                    className={featureIndex % 2 === 1 ? 'rotate-180' : undefined}
                                />
                            </div>
                            <div className="relative mx-auto w-full max-w-[366px] aspect-[366/729]">
                                <img
                                    src={feature.image}
                                    alt={feature.name}
                                    className="h-full w-full object-contain"
                                />
                            </div>
                            <div className="absolute inset-x-0 bottom-0 bg-white/95 p-6 backdrop-blur-sm sm:p-10">
                                <feature.icon className="h-8 w-8" />
                                <h3 className="mt-6 text-sm font-semibold text-gray-900 sm:text-lg">
                                    {feature.name}
                                </h3>
                                <p className="mt-2 text-sm text-gray-500">
                                    {feature.description}
                                </p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <div className="mt-6 flex justify-center gap-3">
                {features.map((_, featureIndex) => (
                    <button
                        type="button"
                        key={featureIndex}
                        className={clsx(
                            'relative h-0.5 w-4 rounded-full',
                            featureIndex === activeIndex ? 'bg-gray-300' : 'bg-gray-500',
                        )}
                        aria-label={`Go to slide ${featureIndex + 1}`}
                        onClick={() => {
                            slideRefs.current[featureIndex].scrollIntoView({
                                block: 'nearest',
                                inline: 'nearest',
                            })
                        }}
                    >
                        <span className="absolute -inset-x-1.5 -inset-y-3" />
                    </button>
                ))}
            </div>
        </>
    )
}

const LandingNew = () => {
    const { user } = useAuthContext();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    if (user) {
        navigate('/home');
        return null;
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <header>
                <nav>
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        <div className="relative z-50 flex justify-between py-8">
                            <div className="relative z-10 flex items-center gap-16">
                                <Link to="/" aria-label="Home" className="flex items-center gap-2">
                                    <BeeIcon className="h-10 w-auto" />
                                    <span className="text-xl font-semibold text-gray-900">Rebee</span>
                                </Link>
                                <div className="hidden lg:flex lg:gap-10">
                                    <a href="#features" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">Features</a>
                                    <a href="#about" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">About</a>
                                    <a href="/privacy-policy" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">Privacy</a>
                                </div>
                            </div>
                            <div className="flex items-center gap-6">
                                <button className="lg:hidden relative z-10 -m-2 inline-flex items-center rounded-lg stroke-gray-900 p-2 hover:bg-gray-200/50 transition-colors" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                                    {mobileMenuOpen ? (
                                        <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                                            <path d="M17 14l-5-5-5 5" />
                                        </svg>
                                    ) : (
                                        <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                                            <path d="M5 6h14M5 18h14M5 12h14" />
                                        </svg>
                                    )}
                                </button>
                                <div className="hidden lg:flex lg:items-center lg:gap-6">
                                    <a href="https://apps.apple.com/us/app/rebee-ai/id6754389955" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-gray-900 py-2.5 px-4 text-sm font-semibold text-white hover:bg-gray-700 transition-all shadow-lg shadow-gray-900/20 hover:shadow-xl hover:shadow-gray-900/30 hover:-translate-y-0.5">
                                        <AppleIcon className="h-5 w-5" />
                                        Download
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                    {mobileMenuOpen && (
                        <div className="absolute inset-x-0 top-0 z-40 origin-top rounded-b-2xl bg-gray-50 px-6 pt-32 pb-6 shadow-2xl shadow-gray-900/20 lg:hidden">
                            <div className="space-y-4">
                                <a href="#features" className="block text-base text-gray-700 hover:text-gray-900" onClick={() => setMobileMenuOpen(false)}>Features</a>
                                <a href="#about" className="block text-base text-gray-700 hover:text-gray-900" onClick={() => setMobileMenuOpen(false)}>About</a>
                                <a href="/privacy-policy" className="block text-base text-gray-700 hover:text-gray-900" onClick={() => setMobileMenuOpen(false)}>Privacy</a>
                            </div>
                            <div className="mt-8 flex flex-col gap-4">
                                <a href="https://apps.apple.com/us/app/rebee-ai/id6754389955" target="_blank" rel="noopener noreferrer" className="inline-flex justify-center items-center gap-2 rounded-lg py-2.5 px-4 text-sm font-semibold bg-gray-900 text-white hover:bg-gray-700 transition-colors">
                                    <AppleIcon className="h-5 w-5" />
                                    Download the app
                                </a>
                            </div>
                        </div>
                    )}
                </nav>
            </header>

            {/* Hero Section */}
            <div className="overflow-hidden py-10 sm:py-20 lg:pb-32 xl:pb-36">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="lg:grid lg:grid-cols-12 lg:gap-x-8 lg:gap-y-20">
                        <div className="relative z-10 mx-auto max-w-2xl lg:col-span-6 lg:max-w-none lg:pt-6 xl:col-span-6">
                            <div className="mb-6 inline-flex items-center rounded-full bg-white p-1 pr-4 ring-1 ring-gray-900/5 shadow-md gap-3">
                                <div className="flex -space-x-0.5 p-1 rounded-full bg-yellow-50/50">
                                    {[1, 2, 3, 4, 5].map((i) => (
                                        <StarIcon key={i} className="h-5 w-5 text-yellow-400" />
                                    ))}
                                </div>
                                <div className="text-sm font-semibold text-gray-900">
                                    Rated 5-stars
                                </div>
                            </div>
                            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
                                Identify your food triggers{' '}
                                <span className="relative whitespace-nowrap">
                                    <svg aria-hidden="true" viewBox="0 0 418 42" className="absolute top-2/3 left-0 h-[0.58em] w-full fill-yellow-300/70" preserveAspectRatio="none">
                                        <path d="M203.371.916c-26.013-2.078-76.686 1.963-124.73 9.946L67.3 12.749C35.421 18.062 18.2 21.766 6.004 25.934 1.244 27.561.828 27.778.874 28.61c.07 1.214.828 1.121 9.595-1.176 9.072-2.377 17.15-3.92 39.246-7.496C123.565 7.986 157.869 4.492 195.942 5.046c7.461.108 19.25 1.696 19.17 2.582-.107 1.183-7.874 4.31-25.75 10.366-21.992 7.45-35.43 12.534-36.701 13.884-2.173 2.308-.202 4.407 4.442 4.734 2.654.187 3.263.157 15.593-.78 35.401-2.686 57.944-3.488 88.365-3.143 46.327.526 75.721 2.23 130.788 7.584 19.787 1.924 20.814 1.98 24.557 1.332l.066-.011c1.201-.203 1.53-1.825.399-2.335-2.911-1.31-4.893-1.604-22.048-3.261-57.509-5.556-87.871-7.36-132.059-7.842-23.239-.254-33.617-.116-50.627.674-11.629.54-42.371 2.494-46.696 2.967-2.359.259 8.133-3.625 26.504-9.81 23.239-7.825 27.934-10.149 28.304-14.005.417-4.348-3.529-6-16.878-7.066Z" />
                                    </svg>
                                    <span className="relative">with AI.</span>
                                </span>
                            </h1>
                            <p className="mt-6 text-lg text-gray-600 leading-8">Snap a photo of your meal and our AI instantly detects ingredients. Track symptoms and discover which foods may be causing your reactions.</p>
                            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-4">
                                <a href="https://apps.apple.com/us/app/rebee-ai/id6754389955" target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 rounded-full bg-gray-900 py-3 px-5 text-sm font-semibold text-white transition-all hover:bg-gray-700 shadow-lg shadow-gray-900/20 hover:shadow-xl hover:shadow-gray-900/30 hover:-translate-y-0.5">
                                    <AppleIcon className="h-5 w-5" />
                                    Download on App Store
                                    <svg className="h-4 w-4 transition-transform group-hover:translate-x-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                </a>
                                <a href="#features" className="inline-flex items-center gap-2 rounded-full py-3 px-5 text-sm font-semibold text-gray-900 ring-1 ring-gray-900/10 transition-all hover:ring-gray-900/20 hover:bg-gray-900/5">
                                    Learn more
                                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M19 9l-7 7-7-7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                </a>
                            </div>

                        </div>
                        {/* Hero Image - Updated to use Device-4 as the transition target or main view if applicable, keeping standard frame */}
                        <div className="relative mt-10 sm:mt-20 lg:col-span-6 lg:row-span-2 lg:mt-0 xl:col-span-6">
                            <BackgroundIllustration className="absolute top-4 left-1/2 h-[1026px] w-[1026px] -translate-x-1/3 stroke-gray-300/70 [mask-image:linear-gradient(to_bottom,white_20%,transparent_75%)] sm:top-16 sm:-translate-x-1/2 lg:-top-16 lg:ml-12 xl:-top-14 xl:ml-0" />
                            <div className="-mx-4 px-9 [mask-image:linear-gradient(to_bottom,white_60%,transparent)] sm:mx-0 lg:absolute lg:-inset-x-10 lg:-top-10 lg:-bottom-20 lg:px-0 lg:pt-10 xl:-bottom-32">
                                <div className="relative flex items-center justify-center gap-4 lg:gap-8 origin-top scale-75 sm:scale-100 lg:scale-110">
                                    {/* First Phone - Scan (Device.png) */}
                                    <div className="relative w-[180px] lg:w-[220px] transform -rotate-6">
                                        <div className="absolute -inset-2 rounded-[2.5rem] bg-gradient-to-tr from-gray-200 via-gray-100 to-white opacity-40 blur-xl" />
                                        <img src="/Device.png" alt="Scanning food" className="relative w-full rounded-[2rem] shadow-2xl shadow-gray-900/20" />
                                    </div>

                                    {/* Arrow */}
                                    <div className="relative z-10 hidden sm:block self-center mt-6">
                                        <svg width="60" height="40" viewBox="0 0 100 60" fill="none" className="text-gray-400 rotate-12">
                                            <path d="M10 30 C 40 10, 60 50, 90 30" stroke="currentColor" strokeWidth="4" fill="none" strokeDasharray="8 4" />
                                            <path d="M90 30 L 80 20 M 90 30 L 80 40" stroke="currentColor" strokeWidth="4" fill="none" />
                                        </svg>
                                    </div>

                                    {/* Second Phone - Breakdown (Device-4.png) */}
                                    <div className="relative w-[180px] lg:w-[220px] transform rotate-6 mt-12 lg:mt-24">
                                        <div className="absolute -inset-2 rounded-[2.5rem] bg-gradient-to-tr from-yellow-300 via-yellow-400 to-orange-400 opacity-20 blur-xl" />
                                        <img src="/Device-4.png" alt="Ingredient Analysis" className="relative w-full rounded-[2rem] shadow-2xl shadow-gray-900/30" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* About / Doctor Section */}
            <section id="about" className="bg-gray-900 py-20 sm:py-32 overflow-hidden">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="mx-auto max-w-2xl lg:max-w-4xl">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">Developed with leading allergists</h2>
                            <p className="mt-4 text-lg text-gray-400">Our approach is backed by clinical expertise and medical best practices.</p>
                        </div>
                        <div className="group relative flex flex-col items-center gap-8 rounded-3xl bg-gray-800 p-8 shadow-xl ring-1 ring-white/10 sm:flex-row sm:p-12 transition-all hover:shadow-2xl">
                            <div className="relative flex-shrink-0">
                                <div className="absolute -inset-2 rounded-full bg-gradient-to-tr from-green-400 to-green-600 opacity-20 blur-lg group-hover:opacity-30 transition-opacity" />
                                <img src="/dr_robert_call.png" alt="Dr. Robert S. Call, M.D." className="relative h-32 w-32 rounded-full object-cover object-top sm:h-40 sm:w-40 ring-4 ring-gray-700 shadow-lg" />
                            </div>
                            <div className="text-center sm:text-left">
                                <span className="inline-flex items-center gap-2 rounded-full bg-green-500/10 px-3 py-1 text-sm font-medium text-green-400 ring-1 ring-inset ring-green-500/20">
                                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                                    Board Certified Allergist
                                </span>
                                <h3 className="mt-4 text-2xl font-bold text-white">Developed with Dr. Robert S. Call, M.D.</h3>
                                <p className="mt-2 text-gray-300 leading-7">Rebee's approach was developed in collaboration with a board certified allergist to ensure our methodology aligns with clinical best practices.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>



            {/* Primary Features Section - Tabbed Interface */}
            <section id="features" aria-label="Features for tracking food sensitivities" className="bg-gray-50 py-20 sm:py-32">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-3xl">
                        <div className="inline-flex items-center gap-2 rounded-full bg-yellow-50 px-4 py-1.5 text-sm font-medium text-yellow-800 mb-6 ring-1 ring-inset ring-yellow-500/20">
                            <span>✨</span> Features
                        </div>
                        <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                            What does Rebee include?
                        </h2>
                        <p className="mt-4 text-lg text-gray-600 leading-8">
                            Rebee was built for people struggling with food sensitivities who want real answers, not guesswork.
                        </p>
                    </div>

                    <div className="mt-16 md:hidden">
                        <FeaturesMobile />
                    </div>
                    <div className="hidden md:mt-20 md:block">
                        <FeaturesDesktop />
                    </div>
                </div>
            </section>

            {/* Why use Rebee Section - Cal AI Style */}
            <section className="bg-white py-24 sm:py-32">
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <div className="mx-auto max-w-2xl text-center">
                        <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-5xl">Why use Rebee?</h2>
                        <p className="mt-6 text-lg leading-8 text-gray-600">
                            Food sensitivity testing is often unreliable. Rebee helps you find answers through the gold standard of elimination and tracking.
                        </p>
                    </div>
                    <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
                        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
                            {/* Card 1 */}
                            <div className="flex flex-col rounded-3xl bg-white p-10 ring-1 ring-gray-200 shadow-xl transition-shadow hover:shadow-2xl">
                                <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-50">
                                    <svg className="h-8 w-8 text-gray-900" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold text-gray-900">Tests are inaccurate</h3>
                                <p className="mt-4 flex-auto text-base leading-7 text-gray-500">
                                    50% of skin and blood tests for food allergies result in false positives, leading to unnecessary dietary restrictions.
                                </p>
                            </div>

                            {/* Card 2 */}
                            <div className="flex flex-col rounded-3xl bg-white p-10 ring-1 ring-gray-200 shadow-xl transition-shadow hover:shadow-2xl">
                                <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-50">
                                    <svg className="h-8 w-8 text-gray-900" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.388 24.388 0 0110.5 0m-10.5 0c1.059.095 2.098.24 3.126.436m0 0V8.25m0-5.146a24.416 24.416 0 00-3.126-.436m3.126.436v1.068c0 .808.236 1.581.665 2.251l4.5 7.024m-11.25 0a2.25 2.25 0 00-2.25 2.25V18c0 .414.336.75.75.75h13.5c.414 0 .75-.336.75-.75v-2.623a2.25 2.25 0 00-2.25-2.25" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold text-gray-900">Some conditions have no test</h3>
                                <p className="mt-4 flex-auto text-base leading-7 text-gray-500">
                                    Complex conditions like FPIES often don't even have a reliable medical test available for diagnosis.
                                </p>
                            </div>

                            {/* Card 3 */}
                            <div className="flex flex-col rounded-3xl bg-white p-10 ring-1 ring-gray-200 shadow-xl transition-shadow hover:shadow-2xl">
                                <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-50">
                                    <svg className="h-8 w-8 text-gray-900" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold text-gray-900">Tracking is the Gold Standard</h3>
                                <p className="mt-4 flex-auto text-base leading-7 text-gray-500">
                                    Keeping a detailed food diary to track meals and reactions is the medical gold standard for identifying triggers.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="relative overflow-hidden bg-gray-900 py-20 sm:py-32">
                <div className="absolute top-1/2 left-20 -translate-y-1/2 sm:left-1/2 sm:-translate-x-1/2">
                    <CircleBackground color="#FACC15" className="animate-[spin_120s_linear_infinite]" />
                </div>
                <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="mx-auto max-w-md text-center">
                        <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">Start understanding your body today</h2>
                        <p className="mt-4 text-lg text-gray-300 leading-8">Download Rebee AI and take the first step toward identifying your food sensitivities.</p>
                        <div className="mt-8 flex justify-center">
                            <a href="https://apps.apple.com/us/app/rebee-ai/id6754389955" target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 rounded-full bg-white py-3 px-5 text-sm font-semibold text-gray-900 transition-all hover:bg-gray-100 shadow-lg hover:shadow-xl hover:-translate-y-0.5">
                                <AppleIcon className="h-5 w-5" />
                                Download on App Store
                                <svg className="h-4 w-4 transition-transform group-hover:translate-x-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-gray-200 bg-gray-50">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
                    <div className="flex flex-col items-start justify-between gap-y-12 lg:flex-row lg:items-center">
                        <div>
                            <div className="flex items-center text-gray-900">
                                <BeeIcon className="h-10 w-10 flex-none" />
                                <div className="ml-4">
                                    <p className="text-base font-semibold">Rebee</p>
                                    <p className="mt-1 text-sm text-gray-500">Identify your food triggers with AI.</p>
                                </div>
                            </div>
                            <nav className="mt-8 flex gap-8">
                                <a href="#features" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">Features</a>
                                <a href="#about" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">About</a>
                                <a href="/privacy-policy" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">Privacy</a>
                                <a href="/terms-of-service" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">Terms</a>
                            </nav>
                        </div>
                        <div className="group relative flex items-center gap-4 rounded-2xl p-4 transition-colors hover:bg-gray-100 lg:self-auto">
                            <a href="https://apps.apple.com/us/app/rebee-ai/id6754389955" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3">
                                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-900 text-white">
                                    <AppleIcon className="h-6 w-6" />
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-gray-900">Download the app</p>
                                    <p className="text-sm text-gray-500">Available on the App Store</p>
                                </div>
                            </a>
                        </div>
                    </div>
                    <div className="mt-12 flex flex-col items-center border-t border-gray-200 pt-8 md:flex-row md:justify-between">
                        <p className="text-sm text-gray-500">© {new Date().getFullYear()} Epigenesis LLC. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div >
    );
};

export default LandingNew;
